import React, { cloneElement, isValidElement, ReactElement } from 'react';
import { css } from '@emotion/react';
import classNames from 'classnames';
import {
  button_base_css,
  button_icon_size_css,
  getIconButtonColorStyle,
  getIconButtonStyle,
  icon_button_size_css,
} from './styles';
import type { IconButtonColor, IconButtonShape, IconButtonSize, IconButtonVariant } from './types';
import { ButtonProps } from './Button';
import { IconProps } from '../icons/generated';
import { Slot, Slottable } from '@radix-ui/react-slot';

export type { IconButtonColor, IconButtonShape, IconButtonVariant } from './types';

export interface IconButtonProps
  extends Omit<ButtonProps, 'loading' | 'leftIcon' | 'rightIcon' | 'variant' | 'color' | 'shape' | 'size'> {
  /** @default 'outlined' */
  variant?: IconButtonVariant;
  /** @default 'secondary' */
  color?: IconButtonColor;
  /**
   * 렌더링 될 아이콘 요소
   *  - Icon 컴포넌트 color props가 없다면 currentColor가 지정됩니다.
   */
  icon?: ReactElement<IconProps>;
  /** @default 'medium' */
  size?: IconButtonSize;
  /** @default 'rounded' */
  shape?: IconButtonShape;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      type = 'button',
      shape = 'rounded',
      variant = 'outlined',
      color = 'secondary',
      size = 'medium',
      fill,
      disabled,
      className,
      icon,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const iconSize = button_icon_size_css[size];
    const _icon = isValidElement(icon)
      ? cloneElement(icon, { color: icon.props.color ?? 'currentColor', size: iconSize })
      : null;
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        type={type}
        className={classNames('pds-button', className)}
        css={[
          button_base_css,
          getIconButtonStyle(shape),
          shape === 'rounded' && variant === 'outlined' && getIconButtonColorStyle(color),
          icon_button_size_css[size],
          css`
            ${!fill && 'flex: none;'}
            ${fill && 'width: 100%;'}
            ${size === 'xxsmall' && shape === 'rounded' && 'border-radius: 6px;'}
            cursor: ${disabled ? 'not-allowed' : 'pointer'};
          `,
        ]}
        disabled={disabled}
        {...props}
        ref={ref}
      >
        <Slottable>{_icon}</Slottable>
      </Comp>
    );
  },
);
